import React, { SFC, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TagModel, TagType, TagUsageInfo, getTagNameByType } from '../../../shared/types/tag'
import { Grid, Paper, IconButton, Tabs, Typography, Tab, Card, ListItem, CardContent } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import { TabPanel } from '../../../shared/components/tabPanel'
import { config } from '../../../config'
import { NoData } from '../../../shared/components/NoData'
import { useHistory } from 'react-router-dom'
import { setUsersSearchAndSelect } from '../../../redux/actions/users'
import { setSelectedLocationId } from '../../../redux/actions/locations'

const useStyles = makeStyles((theme) => ({
    cardWrapper: {
        height: '100%',
        width: `calc(100% - ${theme.spacing(1)})`,
        paddingLeft: theme.spacing(1),
        '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    cardWrapperInner: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    tabContentWrapper: {
        flexGrow: 1,
        overflowY: 'auto',
        height: '100%',
        padding: theme.spacing(3),
    },
    tabContent: {
        height: '100%',
    },
    card: {
        height: '100%',
    },
    titleWrapper: {
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        flex: '0',
    },
    titleText: {
        textAlign: 'left',
    },
    titleButtons: {
        textAlign: 'right',
    },
    profileItem: {
        textAlign: 'left',
    },
    profileValue: {
        paddingLeft: theme.spacing(1),
        textAlign: 'left',
        position: 'relative',
        width: '80%',
        '& span': {
            position: 'absolute',
            top: theme.spacing(0.3),
        },
    },
    groupDivider: {
        height: theme.spacing(1.5),
    },
    sessionDayTitle: {
        marginLeft: theme.spacing(1),
    },
    sessionDayTitleDivider: {
        marginTop: theme.spacing(3),
    },
    sessionListItem: {
        margin: theme.spacing(1, 0, 1, 0),
        padding: theme.spacing(2),
        display: 'flex',
        width: '50%',
    },
    sessionListContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    sessionListContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        '&:last-child': {
            padding: 0,
        },
    },
    sessionImageWrapper: {
        flex: '1 0 auto',
        textAlign: 'right',
        marginLeft: theme.spacing(1),
    },
    sessionImage: {
        width: '80px',
        height: '80px',
    },
    sessionLoadMore: {
        marginBottom: theme.spacing(3),
    },
    exportButton: {
        marginRight: theme.spacing(1),
    },
    fullHeight: {
        height: '20%',
    },
    dataInfoWrapper: {
        textAlign: 'center',
        paddingLeft: theme.spacing(1),
    },
    listItem: {
        margin: theme.spacing(1, 1, 2, 1),
        display: 'flex',
        '& .Mui-selected': {
            borderBottom: `3px solid ${theme.palette.primary.main}`,
            paddingBottom: theme.spacing(2) - 3,
        },
    },
    listItemButton: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'start',
        padding: theme.spacing(2),
    },
    listContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    listContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        '&:last-child': {
            padding: 0,
        },
    },
}))

interface TagCardProps {
    tag: TagModel
    onDelete: Function
    onEdit: Function
}
const TagCard: SFC<TagCardProps> = ({ tag, onDelete, onEdit }) => {
    const classes = useStyles()
    const { t } = useTranslation()
    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const history = useHistory()
    const dispatch = useDispatch()

    const tenants = useSelector((state: any) => state.tenants.value)
    const tenant = tenants?.find((tenant: any) => tenant.id === tag.tenantId)
    const tagsUsage = useSelector((state: any) => state.tags.usage.value)
    const tagsUsageExecuted = useSelector((state: any) => state.tags.usage.executed)

    const [tabIndex, setTabIndex] = useState<number>(0)
    const tagValues = [
        // ['id', tag.id],
        [t('common.name'), tag.name],
        [t('tags.type'), getTagNameByType(tag.type)],
        [t('common.description'), tag.description],
        tag.type === TagType.User ? [t('tags.isExclusive'), tag.isExclusive ? t('common.yes') : t('common.no')] : [],
        tenantId && tenantId === config.mainTenantId ? [t('common.tenant'), tenant?.name ? tenant.name : tag.tenantId] : [],
    ]

    const redirectToEntity = (tagUsage: TagUsageInfo) => {
        switch (tag.type) {
            case TagType.User:
                dispatch(setUsersSearchAndSelect(tagUsage.name)); // Update users.search in Redux
                history.push('/users');
                // here i want to setSearch(tagUsage.name)
                break
            case TagType.Charger:
            case TagType.Location:
                dispatch(setSelectedLocationId(tagUsage.locationId))
                history.push('/locations')
                break
        }
    }

    return (
        <div className={classes.cardWrapper}>
            <Paper elevation={1} className={classes.card}>
                <div className={classes.cardWrapperInner}>
                    <Grid item xs={12} container className={classes.titleWrapper}>
                        <Grid item xs={6} className={classes.titleText}>
                            <Typography variant="h2" style={{ padding: 0 }}>
                                {tag.name}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} className={classes.titleButtons}>
                            <IconButton onClick={() => onEdit()}>
                                <EditIcon />
                            </IconButton>
                            <IconButton style={{ color: 'red' }} onClick={() => onDelete()}>
                                <DeleteIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <div>
                        <Tabs value={tabIndex} onChange={(event: any, newValue: number) => setTabIndex(newValue)}>
                            <Tab label={t('tags.profile')} color="primary" />
                            <Tab label={t('tags.usage')} />
                        </Tabs>
                    </div>
                    <div className={classes.tabContentWrapper}>
                        <TabPanel value={tabIndex} index={0} className={classes.tabContent}>
                            <Grid container className={classes.fullHeight}>
                                {tagValues.map((x: any, index) =>
                                    x.length === 2 ? (
                                        <React.Fragment key={index}>
                                            <Grid item sm={2} className={classes.profileItem}>
                                                <Typography variant="subtitle2">{x[0]}</Typography>
                                            </Grid>
                                            <Grid item sm={10} className={classes.profileValue}>
                                                <Typography variant="caption" component="div">
                                                    {x[1] && x[1] !== '' ? x[1] : '\u00A0'}
                                                </Typography>
                                            </Grid>
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment key={index}>
                                            <Grid item sm={12} className={classes.groupDivider} />
                                            {x.length === 1 && (
                                                <Grid item sm={12} className={classes.profileItem}>
                                                    <Typography variant="h6">{x[0]}</Typography>
                                                </Grid>
                                            )}
                                        </React.Fragment>
                                    )
                                )}
                            </Grid>
                        </TabPanel>

                        <TabPanel value={tabIndex} index={1} className={classes.tabContent}>
                            {tagsUsageExecuted && (!tagsUsage || tagsUsage.length === 0) ? (
                                <div className={classes.dataInfoWrapper}>
                                    <NoData text={t('tags.noTags')} />
                                </div>
                            ) : (
                                (tagsUsage || []).map((tag: TagUsageInfo) => (
                                    <Card key={tag.tagId} className={classes.listItem} elevation={1}>
                                        <ListItem
                                            button
                                            onClick={() => redirectToEntity(tag)}
                                            className={classes.listItemButton}
                                        >
                                            <div className={classes.listContentWrapper}>
                                                <CardContent className={classes.listContent}>
                                                    <Typography variant="h6">{tag.name}</Typography>
                                                </CardContent>
                                            </div>
                                        </ListItem>
                                    </Card>
                                ))
                            )}
                        </TabPanel>
                    </div>
                </div>
            </Paper>
        </div>
    )
}

export default TagCard
